import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Search, Edit2, Calendar, Ban, CheckCircle, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function EditUserModal({ user, onClose, onSave, token }) {
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    planType: user.planType,
    planExpiryDate: user.planExpiryDate ? new Date(user.planExpiryDate).toISOString().split('T')[0] : '',
    isActive: user.isActive,
    isTrialActive: user.isTrialActive || false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.put(`${API_URL}/admin/users/${user._id}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={mStyles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={mStyles.modal}>
        <h2 style={mStyles.title}>Edit User</h2>
        {error && <div style={mStyles.error}>{error}</div>}

        {[
          { label: 'Full Name', key: 'name', type: 'text' },
          { label: 'Email', key: 'email', type: 'email' },
        ].map(f => (
          <div key={f.key} style={mStyles.field}>
            <label style={mStyles.label}>{f.label}</label>
            <input
              type={f.type}
              value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              style={mStyles.input}
            />
          </div>
        ))}

        <div style={mStyles.field}>
          <label style={mStyles.label}>Plan</label>
          <select 
            value={form.planType} 
            onChange={e => {
              const newPlan = e.target.value;
              setForm(p => ({ 
                ...p, 
                planType: newPlan,
                // Automatically uncheck trial if they are moved off starter
                ...(newPlan !== 'starter' && { isTrialActive: false })
              }));
            }} 
            style={mStyles.input}
          >
            <option value="starter" style={{background: '#1e293b', color: '#fff'}}>Starter</option>
            <option value="professional" style={{background: '#1e293b', color: '#fff'}}>Professional</option>
            <option value="enterprise" style={{background: '#1e293b', color: '#fff'}}>Enterprise</option>
          </select>
        </div>

        {form.planType === 'starter' && (
          <div style={mStyles.field}>
            <label style={{ ...mStyles.label, display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isTrialActive} onChange={e => setForm(p => ({ ...p, isTrialActive: e.target.checked }))} />
              Is on Trial
            </label>
          </div>
        )}

        <div style={mStyles.field}>
          <label style={mStyles.label}>Plan Expiry Date</label>
          <input
            type="date"
            value={form.planExpiryDate}
            onChange={e => setForm(p => ({ ...p, planExpiryDate: e.target.value }))}
            style={mStyles.input}
          />
        </div>

        <div style={mStyles.field}>
          <label style={{ ...mStyles.label, display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
            Account Active
          </label>
        </div>

        <div style={mStyles.actions}>
          <button onClick={onClose} style={mStyles.cancelBtn}>Cancel</button>
          <button onClick={handleSave} disabled={loading} style={mStyles.saveBtn}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExtendModal({ user, onClose, onSave, token }) {
  const [days, setDays] = useState(30);
  const [planType, setPlanType] = useState(user.planType);
  const [loading, setLoading] = useState(false);

  const handleExtend = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/admin/users/${user._id}/extend`, { days, planType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSave();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to extend');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={mStyles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={mStyles.modal}>
        <h2 style={mStyles.title}>Extend Plan for {user.name}</h2>
        <div style={mStyles.field}>
          <label style={mStyles.label}>Plan Type</label>
          <select value={planType} onChange={e => setPlanType(e.target.value)} style={mStyles.input}>
            <option value="starter" style={{background: '#1e293b', color: '#fff'}}>Starter</option>
            <option value="professional" style={{background: '#1e293b', color: '#fff'}}>Professional</option>
            <option value="enterprise" style={{background: '#1e293b', color: '#fff'}}>Enterprise</option>
          </select>
        </div>
        <div style={mStyles.field}>
          <label style={mStyles.label}>Days to Extend</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[7, 14, 30, 90, 365].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                style={{
                  padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                  background: days === d ? '#f59e0b' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${days === d ? '#f59e0b' : 'rgba(255,255,255,0.1)'}`,
                  color: days === d ? '#000' : '#fff'
                }}
              >
                {d}d
              </button>
            ))}
          </div>
          <input type="number" value={days} onChange={e => setDays(Number(e.target.value))} style={{ ...mStyles.input, marginTop: '8px' }} min={1} />
        </div>
        <div style={mStyles.actions}>
          <button onClick={onClose} style={mStyles.cancelBtn}>Cancel</button>
          <button onClick={handleExtend} disabled={loading} style={mStyles.saveBtn}>
            {loading ? 'Extending...' : `Extend by ${days} Days`}
          </button>
        </div>
      </div>
    </div>
  );
}

const PLAN_COLORS = { starter: '#22c55e', professional: '#3b82f6', enterprise: '#a855f7' };

const formatFullDateTime = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

export default function AdminUsers() {
  const { adminToken } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [extendUser, setExtendUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/users`, {
        params: { page, limit: 20, search, plan: planFilter, status: statusFilter },
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsers(res.data.users);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [adminToken, search, planFilter, statusFilter]);

  useEffect(() => { fetchUsers(1); }, [fetchUsers]);

  const handleToggle = async (user) => {
    try {
      await axios.put(`${API_URL}/admin/users/${user._id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      fetchUsers(pagination.page);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setDeleteConfirm(null);
      fetchUsers(pagination.page);
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const isPlanExpired = (date) => date && new Date(date) < new Date();

  return (
    <AdminLayout>
      {editUser && (
        <EditUserModal
          user={editUser}
          token={adminToken}
          onClose={() => setEditUser(null)}
          onSave={() => { setEditUser(null); fetchUsers(pagination.page); }}
        />
      )}
      {extendUser && (
        <ExtendModal
          user={extendUser}
          token={adminToken}
          onClose={() => setExtendUser(null)}
          onSave={() => { setExtendUser(null); fetchUsers(pagination.page); }}
        />
      )}
      {deleteConfirm && (
        <div style={mStyles.overlay}>
          <div style={{ ...mStyles.modal, maxWidth: '380px', textAlign: 'center' }}>
            <div style={{ marginBottom: '12px' }}><Trash2 size={40} color="#ef4444" /></div>
            <h2 style={mStyles.title}>Delete User?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              This will permanently delete <strong style={{ color: '#fff' }}>{deleteConfirm.name}</strong> and all their invoices. This cannot be undone.
            </p>
            <div style={mStyles.actions}>
              <button onClick={() => setDeleteConfirm(null)} style={mStyles.cancelBtn}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm._id)} style={{ ...mStyles.saveBtn, background: '#ef4444' }}>Delete Forever</button>
            </div>
          </div>
        </div>
      )}

      <div>
        <h1 style={styles.pageTitle}>Users Management</h1>
        <p style={styles.pageSub}>Total: <strong>{pagination.total}</strong> users</p>

        {/* Filters */}
        <div style={styles.filters}>
          <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '12px', color: 'rgba(255,255,255,0.4)' }} />
            <input
              type="text"
              placeholder="Search name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...styles.searchInput, paddingLeft: '36px', width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} style={styles.filterSelect}>
            <option value="" style={{background: '#1e293b', color: '#fff'}}>All Plans</option>
            <option value="starter" style={{background: '#1e293b', color: '#fff'}}>Starter</option>
            <option value="professional" style={{background: '#1e293b', color: '#fff'}}>Professional</option>
            <option value="enterprise" style={{background: '#1e293b', color: '#fff'}}>Enterprise</option>
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={styles.filterSelect}>
            <option value="" style={{background: '#1e293b', color: '#fff'}}>All Status</option>
            <option value="active" style={{background: '#1e293b', color: '#fff'}}>Active</option>
            <option value="trial" style={{background: '#1e293b', color: '#fff'}}>Trial</option>
            <option value="expired" style={{background: '#1e293b', color: '#fff'}}>Expired</option>
            <option value="banned" style={{background: '#1e293b', color: '#fff'}}>Banned</option>
          </select>
        </div>

        {/* Table */}
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                {['User', 'Plan', 'Expiry', 'Status', 'Joined', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={styles.emptyTd}>⏳ Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} style={styles.emptyTd}>No users found.</td></tr>
              ) : users.map(u => (
                <tr key={u._id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.userCell}>
                      <div style={styles.avatar}>{u.name?.[0]?.toUpperCase()}</div>
                      <div>
                        <div style={styles.userName}>{u.name}</div>
                        <div style={styles.userEmail}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    {u.isTrialActive ? (
                      <span style={{ ...styles.planBadge, background: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b40' }}>
                        Trial (Starter)
                      </span>
                    ) : (
                      <span style={{ ...styles.planBadge, background: `${PLAN_COLORS[u.planType]}20`, color: PLAN_COLORS[u.planType] || '#fff', border: `1px solid ${PLAN_COLORS[u.planType]}40` }}>
                        {u.planType}
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: isPlanExpired(u.planExpiryDate) ? '#ef4444' : 'rgba(255,255,255,0.85)' }}>
                      {formatFullDateTime(u.planExpiryDate)}
                      {isPlanExpired(u.planExpiryDate) && <span style={{ marginLeft: '6px', fontSize: '12px', padding: '2px 6px', background: '#ef444420', borderRadius: '4px' }}>Expired</span>}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, background: u.isActive ? '#22c55e20' : '#ef444420', color: u.isActive ? '#22c55e' : '#ef4444', border: `1px solid ${u.isActive ? '#22c55e40' : '#ef444440'}` }}>
                      {u.isActive ? '● Active' : '● Banned'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{...styles.dateText, color: 'rgba(255,255,255,0.85)'}}>{formatFullDateTime(u.createdAt)}</span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <button onClick={() => setEditUser(u)} style={styles.actionBtn} title="Edit"><Edit2 size={16} /></button>
                      <button onClick={() => setExtendUser(u)} style={styles.actionBtn} title="Extend Plan"><Calendar size={16} /></button>
                      <button onClick={() => handleToggle(u)} style={{ ...styles.actionBtn, color: u.isActive ? '#ef4444' : '#10b981' }} title={u.isActive ? 'Ban' : 'Unban'}>
                        {u.isActive ? <Ban size={16} /> : <CheckCircle size={16} />}
                      </button>
                      <button onClick={() => setDeleteConfirm(u)} style={{ ...styles.actionBtn, color: '#ef4444' }} title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={styles.pagination}>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => fetchUsers(p)}
                style={{ ...styles.pageBtn, background: p === pagination.page ? '#f59e0b' : 'rgba(255,255,255,0.06)', color: p === pagination.page ? '#000' : '#fff' }}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const styles = {
  pageTitle: { fontSize: '24px', fontWeight: '700', color: '#fff', margin: '0 0 6px' },
  pageSub: { fontSize: '14px', color: 'rgba(255,255,255,0.4)', margin: '0 0 20px' },
  filters: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  searchInput: {
    flex: 1, minWidth: '220px',
    padding: '10px 16px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
  },
  filterSelect: {
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
  },
  tableWrapper: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '12px',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'rgba(255,255,255,0.05)' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderTop: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' },
  td: { padding: '14px 16px', verticalAlign: 'middle' },
  emptyTd: { padding: '48px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '15px' },
  userCell: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: '700', fontSize: '14px', flexShrink: 0,
  },
  userName: { fontSize: '14px', fontWeight: '600', color: '#fff' },
  userEmail: { fontSize: '12px', color: 'rgba(255,255,255,0.4)' },
  planBadge: { fontSize: '12px', fontWeight: '600', padding: '4px 10px', borderRadius: '100px', textTransform: 'capitalize' },
  statusBadge: { fontSize: '12px', fontWeight: '600', padding: '4px 10px', borderRadius: '100px' },
  dateText: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
  actions: { display: 'flex', gap: '4px' },
  actionBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', borderRadius: '7px', padding: '6px 8px', cursor: 'pointer', fontSize: '16px', transition: 'background 0.15s' },
  pagination: { display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' },
  pageBtn: { width: 36, height: 36, borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px' },
};

const mStyles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 999999,
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: '#1a1a2e',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    padding: '32px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  title: { fontSize: '20px', fontWeight: '700', color: '#fff', margin: '0 0 20px' },
  error: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px', fontWeight: '500' },
  input: { width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  actions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' },
  cancelBtn: { padding: '10px 20px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
  saveBtn: { padding: '10px 24px', background: '#f59e0b', border: 'none', color: '#000', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' },
};
